import type {
  Resource,
  ResourceDocument,
  ResourceDocumentApi,
  ResourceNetwork,
  ResourceNetworkApi,
  Talent,
} from '@/types/resource.types.ts'
import type { FiltersApiConfig } from '@/types/filter.types.ts'
import type { ConsultantFilters } from '@/utils/apiUtils.ts'
import type { PaginationOptions } from '@/types/api.types'
import { CLEVERLINK_API_BASE_URL } from '@/constants/apiConstants.ts'
import {
  resourceSearchWithPagination,
  resourceWithPagination,
  resourceWithSecureFetch,
  resourceWithSecureMutation,
  secureFetch,
} from '@/utils/apiUtils.ts'

type RouteTalentId = Talent['unified_identity_id']

export const resourcesRoutes = {
  root: `${CLEVERLINK_API_BASE_URL}/resources`,
  list() {
    return resourcesRoutes.root
  },
  filters() {
    return `${resourcesRoutes.root}/filters`
  },
  search() {
    return `${resourcesRoutes.root}/search`
  },
}

export const identitiesRoutes = {
  root: `${CLEVERLINK_API_BASE_URL}/identities`,
  talent(talentId: RouteTalentId) {
    return `${identitiesRoutes.root}/${talentId}/talent`
  },
  network(talentId: RouteTalentId) {
    return `${identitiesRoutes.root}/${talentId}/network`
  },
  favorite(talentId: RouteTalentId) {
    return `${identitiesRoutes.root}/${talentId}/favorite`
  },
}

export type FiltersConfigParams = Omit<ConsultantFilters, 'rtype'>

const fetchFiltersConfig = async (
  rtype: string,
  filters?: FiltersConfigParams,
): Promise<FiltersApiConfig> => {
  const url = new URL(resourcesRoutes.filters(), window.location.origin)
  const body: Record<string, unknown> = { rtype }
  if (filters?.typeContract) body.type_contract = filters.typeContract
  if (filters?.desiredDailyRateMin)
    body.desired_daily_rate_min = filters.desiredDailyRateMin
  if (filters?.desiredDailyRateMax)
    body.desired_daily_rate_max = filters.desiredDailyRateMax
  if (filters?.desiredSalaryMin)
    body.desired_salary_min = filters.desiredSalaryMin
  if (filters?.desiredSalaryMax)
    body.desired_salary_max = filters.desiredSalaryMax
  if (filters?.city) body.town = filters.city
  if (filters?.remoteOnly) body.remote_only = filters.remoteOnly
  if (filters?.experiences?.length) body.experiences = filters.experiences
  if (filters?.skills?.length) body.skills = filters.skills
  if (filters?.availabilities?.length)
    body.availabilities = filters.availabilities

  return secureFetch<FiltersApiConfig>(url, 'filters config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export const fetchCandidatesFiltersConfig = (filters?: FiltersConfigParams) =>
  fetchFiltersConfig('candidate', filters)

export const fetchConsultantsFiltersConfig = (filters?: FiltersConfigParams) =>
  fetchFiltersConfig('consultant', filters)

export const fetchResources = resourceWithPagination<Resource>(
  resourcesRoutes.list,
  'resources',
)

export const searchResources = resourceSearchWithPagination<Resource>(
  resourcesRoutes.search,
  'resources',
)

// Fonction générique partagée
const fetchResourcesByType = (type: 'candidate' | 'consultant') => {
  return (
    pagination?: PaginationOptions,
    query?: string,
    sort?: string,
    filters?: ConsultantFilters,
  ) => {
    const hasSearchCriteria =
      query ||
      sort ||
      filters?.typeContract ||
      filters?.desiredDailyRateMin ||
      filters?.desiredDailyRateMax ||
      filters?.desiredSalaryMin ||
      filters?.desiredSalaryMax ||
      filters?.cleverscoreMin ||
      filters?.city ||
      filters?.remoteOnly ||
      filters?.favoritesOnly ||
      (filters?.experiences && filters.experiences.length > 0) ||
      (filters?.skills && filters.skills.length > 0) ||
      (filters?.availabilities && filters.availabilities.length > 0)

    if (hasSearchCriteria) {
      return searchResources(pagination, query, sort, {
        ...filters,
        rtype: type,
      })
    }

    return fetchResources(pagination, undefined, undefined, { rtype: type })
  }
}

export const fetchCandidates = fetchResourcesByType('candidate')
export const fetchConsultants = fetchResourcesByType('consultant')

export const mapResourceDocument = (
  raw: ResourceDocumentApi,
): ResourceDocument => ({
  id: raw.id,
  name: raw.name,
  documentType: raw.document_type,
  url: raw.url,
  uploadedAt: raw.uploaded_at,
})

type RawTalent = Omit<Talent, 'documents'> & {
  documents?: Array<ResourceDocumentApi>
}

const fetchRawTalent = resourceWithSecureFetch<RawTalent, RouteTalentId>(
  identitiesRoutes.talent,
  'talent',
)

export const fetchTalent = async (id: RouteTalentId): Promise<Talent> => {
  const raw = await fetchRawTalent(id)

  return {
    ...raw,
    documents: raw.documents?.map(mapResourceDocument),
  }
}

const fetchTalentNetworkRaw = resourceWithSecureFetch<
  ResourceNetworkApi,
  RouteTalentId
>(identitiesRoutes.network, 'talent network')

export const fetchTalentNetwork = async (
  id: RouteTalentId,
): Promise<ResourceNetwork> => {
  const raw = await fetchTalentNetworkRaw(id)

  return {
    referredBy: raw.referred_by,
    referrals: raw.referred,
    contacts: raw.direct_contacts,
  }
}

export const addTalentFavorite = resourceWithSecureMutation<RouteTalentId>(
  identitiesRoutes.favorite,
  'POST',
  'talent favorite',
)

export const removeTalentFavorite = resourceWithSecureMutation<RouteTalentId>(
  identitiesRoutes.favorite,
  'DELETE',
  'talent favorite',
)
